/**
 * Centralized formatting utilities
 * Use these instead of defining local formatting functions in components
 */

/**
 * Format currency amount with locale and currency code
 * @param amount - The amount to format (in base units, not cents)
 * @param currency - Currency code (default: EUR)
 * @param locale - Locale for formatting (default: es-ES)
 */
export const formatCurrency = (
  amount: number,
  currency: string = "EUR",
  locale: string = "es-ES"
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
};

/**
 * Format amount from cents to display currency
 * @param amountCents - The amount in cents
 * @param currency - Currency code (default: EUR)
 * @param locale - Locale for formatting (default: es-ES)
 */
export const formatAmountCents = (
  amountCents: number,
  currency: string = "EUR",
  locale: string = "es-ES"
): string => {
  return formatCurrency(amountCents / 100, currency, locale);
};

/**
 * Format a date using Spanish locale
 * @param date - Date string, timestamp (in seconds), or Date object
 * @param options - Intl.DateTimeFormat options
 */
export const formatDate = (
  date: string | number | Date,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }
): string => {
  let dateObj: Date;
  
  if (typeof date === "number") {
    // Assume seconds if less than year 3000 in milliseconds
    dateObj = date < 100000000000 ? new Date(date * 1000) : new Date(date);
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }
  
  return dateObj.toLocaleDateString("es-ES", options);
};

/**
 * Format a date with day of week
 * @param date - Date string or Date object
 */
export const formatDateWithWeekday = (date: string | Date): string => {
  return formatDate(date, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Format a date with time
 * @param date - Date string, timestamp, or Date object
 */
export const formatDateTime = (
  date: string | number | Date,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }
): string => {
  let dateObj: Date;
  
  if (typeof date === "number") {
    dateObj = date < 100000000000 ? new Date(date * 1000) : new Date(date);
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }
  
  return dateObj.toLocaleDateString("es-ES", options);
};

/**
 * Format relative time (hace Xs, hace Xm, etc.)
 * @param date - Date to compare against now
 */
export const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return `hace ${diffSec}s`;
  if (diffMin < 60) return `hace ${diffMin}m`;
  if (diffHour < 24) return `hace ${diffHour}h`;
  return `hace ${diffDay}d`;
};

/**
 * Generate URL-friendly slug from text
 * @param text - Text to convert to slug
 */
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param lat1 - Latitude of point 1
 * @param lon1 - Longitude of point 1
 * @param lat2 - Latitude of point 2
 * @param lon2 - Longitude of point 2
 * @returns Distance in kilometers
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Format distance for display
 * @param distanceKm - Distance in kilometers
 */
export const formatDistance = (distanceKm: number): string => {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
};
